import { GAME_WIDTH, GAME_HEIGHT } from '../config.js';
import { lockInput, unlockInput } from './inputLock.js';
import { audioManager } from '../audio/AudioManager.js';
import { gameState } from '../state/gameState.js';
import { NatalkaSprite } from './NatalkaSprite.js';
import { ANIM_ENABLED } from './walkAnim.js';
import { licenceFields } from './licenceFields.mjs';
import { iconText } from './iconText.js';
import { enlargeForTouch } from './touchEnlarge.js';
import { friendPortrait } from './friendPortrait.js';
import { isDefaultLook } from '../look/lookCatalog.mjs';
import { cropForAspect } from './portraitCrop.mjs';

// „Moje prawo jazdy". With a licence: a card drawn like the Polish one — pink ground with a
// guilloche of thin lines, the EU-flag badge with PL, PRAWO JAZDY / RZECZPOSPOLITA POLSKA,
// the photo on the left, the numbered fields (1 surname, 2 name, 4a-4d, 5, 7 signature),
// a PL watermark and „9. B" at the foot. Without: the checklist (kurs / teoria / lekcje /
// praktyka). Everything is drawn in Phaser (no image asset), so it scales with the card.

const CARD_W = 760, CARD_H = 480;
const PINK = 0xf2c6cf, PINK_DEEP = 0xe39fae, LINE = 0xd98da0;
const EU_BLUE = 0x003399, EU_STAR = 0xffcc00;
const FONT = 'system-ui';

function guilloche(scene, x0, y0, w, h) {
  // Thin wavy lines across the card, like the security print: cheap, and it reads as "document".
  const g = scene.add.graphics();
  g.lineStyle(1, LINE, 0.35);
  for (let i = 0; i < 26; i++) {
    const y = y0 + 18 + i * (h - 36) / 25;
    g.beginPath();
    for (let x = 0; x <= w; x += 8) {
      const yy = y + Math.sin((x / w) * Math.PI * 6 + i * 0.7) * 5;
      if (x === 0) g.moveTo(x0 + x, yy); else g.lineTo(x0 + x, yy);
    }
    g.strokePath();
  }
  return g;
}

function euBadge(scene, x, y) {
  // The ring of twelve stars centred on the badge, a white PL in the middle of the ring —
  // the way it is printed on the card.
  const c = scene.add.container(x, y);
  const w = 118, h = 84, cx = w / 2, cy = h / 2, r = 30;
  c.add(scene.add.rectangle(0, 0, w, h, EU_BLUE).setOrigin(0, 0));
  for (let i = 0; i < 12; i++) {
    const a = (i / 12) * Math.PI * 2;
    c.add(scene.add.star(cx + Math.cos(a) * r, cy + Math.sin(a) * r, 5, 1.6, 3.6, EU_STAR));
  }
  c.add(iconText(scene, cx, cy + 1, 'PL', { fontFamily: FONT, fontSize: '22px', color: '#ffffff', fontStyle: 'bold' }).setOrigin(0.5));
  return c;
}

function drawCard(scene, layer, x, y, d) {
  const L = x - CARD_W / 2, T = y - CARD_H / 2;
  const name = window.__myName ?? gameState.raw?.player?.name ?? 'Kierowca';
  const f = licenceFields({ name, licenceIso: d.licence_iso, playerId: window.__myId ?? '' });

  const card = scene.add.graphics();
  card.fillStyle(PINK, 1).fillRoundedRect(L, T, CARD_W, CARD_H, 18);
  card.fillStyle(PINK_DEEP, 0.35).fillRoundedRect(L + CARD_W * 0.55, T, CARD_W * 0.45, CARD_H, { tl: 0, tr: 18, br: 18, bl: 0 });
  card.lineStyle(3, 0xb87a89, 1).strokeRoundedRect(L, T, CARD_W, CARD_H, 18);
  layer.add(card);
  layer.add(guilloche(scene, L, T, CARD_W, CARD_H));

  // Header
  layer.add(euBadge(scene, L + 22, T + 20));
  layer.add(iconText(scene, L + 158, T + 20, 'PRAWO JAZDY', { fontFamily: FONT, fontSize: '30px', color: '#1f43a8', fontStyle: 'bold' }).setOrigin(0, 0));
  layer.add(iconText(scene, L + CARD_W - 24, T + 18, 'RZECZPOSPOLITA\nPOLSKA', { fontFamily: FONT, fontSize: '26px', color: '#1f8a3b', fontStyle: 'bold', align: 'right', lineSpacing: -4 }).setOrigin(1, 0));

  // Photo: the character, masked to a passport frame (head and shoulders).
  const px = L + 96, py = T + 250;
  const frame = scene.add.rectangle(px, py, 150, 200, 0xffffff).setStrokeStyle(2, 0xb87a89);
  layer.add(frame);
  const character = window.__myCharacter ?? 'natalka';
  const equipped = gameState.raw?.inventory?.equipped;
  const look = window.__myLook ?? null;
  if (ANIM_ENABLED || character === 'ken' || !isDefaultLook(character, look)) {
    // Ken's clothes exist only as walk sheets (no flat art): his photo is cut from them — the front standing pose
    // in what he wears now, as the friends panel draws him — scaled into the 148×198 passport frame. A look of her
    // own takes the same path: the flat art has no mask, the portrait recolours. Natalka too (plan 2): her new
    // eyes are in the walk art only; the flat-art photo below (?anim=0) keeps the old ones.
    friendPortrait(scene, { character, outfit: { equipped, look } }, cropForAspect(148 / 198)).then((url) => {
      if (!url || !layer.active) return;
      const img = new Image();
      img.onload = () => {
        if (!layer.active) return;
        const key = `licence_photo_${Date.now()}`;
        scene.textures.addImage(key, img);
        layer.addAt(scene.add.image(px, py, key).setDisplaySize(148, 198), layer.getIndex(frame) + 1);
      };
      img.src = url;
    });
  } else {
    const face = new NatalkaSprite(scene, px, py + 120, {
      // walkAnim off: the photo is a hand-placed crop of the flat art behind a passport mask,
      // and the walk sheets sit at a different height inside the sprite.
      noTweakHud: true, trackIdle: false, walkAnim: false, character,
    });
    face.setOutfit(equipped);
    face.setScale(1.25);
    const maskG = scene.make.graphics({ x: 0, y: 0, add: false });
    maskG.fillStyle(0xffffff).fillRect(px - 74, py - 99, 148, 198);
    face.setMask(maskG.createGeometryMask());
    layer.add(face);
  }
  // The personal number runs up the photo's left edge, right against the frame.
  layer.add(iconText(scene, px - 78, py + 100, f.personalNo, { fontFamily: FONT, fontSize: '12px', color: '#1f1f2b' }).setOrigin(0, 1).setAngle(-90));

  // Numbered fields
  const fx = L + 190, fy = T + 118;
  const row = (dy, label, value, size = 21, bold = true) => {
    layer.add(iconText(scene, fx, fy + dy, label, { fontFamily: FONT, fontSize: `${size}px`, color: '#1f1f2b' }).setOrigin(0, 0));
    layer.add(iconText(scene, fx + 46, fy + dy, value, { fontFamily: FONT, fontSize: `${size}px`, color: '#1f1f2b', fontStyle: bold ? 'bold' : 'normal' }).setOrigin(0, 0));
  };
  row(0, '1.', f.surname, 24);
  row(34, '2.', f.name, 24);
  row(72, '4a.', f.issued, 19, false);
  row(98, '4b.', f.expires, 19, false);
  row(124, '4c.', f.issuer, 19, false);
  layer.add(iconText(scene, fx, fy + 168, '7.', { fontFamily: FONT, fontSize: '19px', color: '#1f1f2b' }).setOrigin(0, 0));
  layer.add(iconText(scene, fx + 46, fy + 160, f.name, { fontFamily: 'cursive', fontSize: '30px', color: '#1f1f2b', fontStyle: 'italic' }).setOrigin(0, 0));

  // Watermark and category
  layer.add(iconText(scene, L + CARD_W - 120, T + CARD_H - 120, 'PL', { fontFamily: FONT, fontSize: '96px', color: '#d98da0', fontStyle: 'bold' }).setOrigin(0.5).setAlpha(0.35));
  layer.add(iconText(scene, L + 24, T + CARD_H - 44, '9.', { fontFamily: FONT, fontSize: '22px', color: '#1f1f2b' }).setOrigin(0, 0.5));
  layer.add(iconText(scene, L + 54, T + CARD_H - 44, f.category, { fontFamily: FONT, fontSize: '26px', color: '#1f1f2b', fontStyle: 'bold' }).setOrigin(0, 0.5));
  layer.add(iconText(scene, L + 100, T + CARD_H - 44, '{car}  uprawnia do kupna i prowadzenia samochodu', { fontFamily: FONT, fontSize: '15px', color: '#3a2e33' }).setOrigin(0, 0.5));
}

function drawChecklist(scene, layer, x, y, d) {
  const W = 640, H = 400;
  layer.add(scene.add.rectangle(x, y, W, H, 0xffffff).setStrokeStyle(4, 0x8a6d3b));
  layer.add(iconText(scene, x - W / 2 + 24, y - H / 2 + 18, '{licence} Moje prawo jazdy',
    { fontFamily: FONT, fontSize: '22px', color: '#2e2116', fontStyle: 'bold' }).setOrigin(0, 0));
  const rows = [
    ['Kurs', d.course_bought], ['Egzamin teoretyczny', d.theory_passed],
    [`Lekcje praktyczne (${d.lessons_done}/${d.lessons_required})`, d.lessons_done >= d.lessons_required],
    ['Egzamin praktyczny', d.practical_passed],
  ];
  layer.add(iconText(scene, x - W / 2 + 40, y - H / 2 + 80,
    `Jeszcze nie masz prawa jazdy.\n\n${rows.map(([l, ok]) => `${ok ? '{check}' : '{box_empty}'} ${l}`).join('\n')}`,
    { fontFamily: FONT, fontSize: '22px', color: '#222', lineSpacing: 10 }).setOrigin(0, 0));
  return { W, H };
}

export function showLicenceModal(scene) {
  return new Promise((resolve) => {
    const cam = scene.cameras.main;
    const x = cam.scrollX + GAME_WIDTH / 2, y = cam.scrollY + GAME_HEIGHT / 2;
    const layer = scene.add.container(0, 0).setDepth(2000);
    lockInput(scene);
    const d = gameState.raw.driving;
    const overlay = scene.add.rectangle(cam.scrollX, cam.scrollY, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.6).setOrigin(0, 0).setInteractive();
    layer.add(overlay);
    let W = CARD_W, H = CARD_H;
    if (d.has_licence) drawCard(scene, layer, x, y, d);
    else ({ W, H } = drawChecklist(scene, layer, x, y, d));
    const closeBtn = iconText(scene, x + W / 2 - 14, y - H / 2 - 34, '×', { fontFamily: FONT, fontSize: '28px', color: '#ffffff', fontStyle: 'bold' }).setOrigin(1, 0).setInteractive({ useHandCursor: true });
    layer.add(closeBtn);
    enlargeForTouch(scene, layer, { x, y, w: W, h: H + 80 }, { overlay });
    const close = () => { audioManager.playSfx('click'); unlockInput(scene); layer.destroy(); resolve(); };
    closeBtn.on('pointerup', close);
    overlay.on('pointerup', close);
  });
}
